import React, { useId } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import ChartDefs from './ChartDefs';
import ChartTooltip from './ChartTooltip';
import useChartTheme from './useChartTheme';
import { makeChartIds } from './chartTokens';

/* ──────────────────────────────────────────────────────────────────────────
   GlowArea — area/line recipe (§3): smooth monotone curves, gradient strokes,
   soft gradient area fills, shared glow. Multi-series via `series`, each tone
   ('gold' | 'pos' | 'neg') getting its own gradient def + the shared glow filter.
   ────────────────────────────────────────────────────────────────────────── */

/* Custom legend — Recharts derives its swatch colour from the series stroke, which
   here is a url(#gradient) ref; render our own glowing dots from resolved colours. */
const GlowLegend = ({ payload, colorByKey }) => (
  <div className="flex items-center justify-center gap-4 pt-2 text-xs text-arc-ink-3">
    {payload?.map((e) => {
      const color = colorByKey[e.dataKey] || e.color;
      return (
        <span key={e.value} className="flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: color, boxShadow: `0 0 6px ${color}` }}
          />
          {e.value}
        </span>
      );
    })}
  </div>
);

/* Marker dot — a glowing point overlaid on the line to flag an event (e.g. an
   external deposit/withdrawal). Native <title> gives an accessible hover tooltip
   without a chart-level Tooltip; the paper-coloured ring lifts it off the line. */
const FlowMarker = ({ cx, cy, color, glowId, ring, label }) => {
  if (cx == null || cy == null) return null;
  return (
    <g style={{ pointerEvents: 'auto' }}>
      {label && <title>{label}</title>}
      <circle cx={cx} cy={cy} r={4} fill={color} stroke={ring} strokeWidth={1.5} filter={`url(#${glowId})`} />
    </g>
  );
};

const GlowArea = ({
  data,
  series,                 // [{ key, name, tone }]
  xKey,
  height = 300,
  yTickFormatter,
  valueFormatter,
  fillSeries = true,      // soft area fills under each series
  markers = [],           // [{ key, x, y, tone, label }] overlaid event dots
}) => {
  const ids = makeChartIds(useId());
  const colors = useChartTheme();

  const colorByKey = {};
  series.forEach((s) => { colorByKey[s.key] = colors.tone[s.tone]; });

  return (
    <>
      <ChartDefs ids={ids} colors={colors} />
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={colors.line} />
          <XAxis dataKey={xKey} tick={{ fill: colors.ink3, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            tick={{ fill: colors.ink3, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
            tickFormatter={yTickFormatter}
          />
          <Tooltip
            cursor={{ stroke: colors.accent, strokeOpacity: 0.4 }}
            content={<ChartTooltip valueFormatter={valueFormatter} getColor={(p) => colorByKey[p.dataKey]} />}
          />
          <Legend content={<GlowLegend colorByKey={colorByKey} />} />
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={`url(#${ids.line[s.tone]})`}
              strokeWidth={2.5}
              fill={fillSeries ? `url(#${ids.area[s.tone]})` : 'none'}
              filter={`url(#${ids.glow})`}
              dot={false}
              activeDot={{ r: 4, fill: colors.tone[s.tone], filter: `url(#${ids.glow})` }}
            />
          ))}
          {markers.map((m) => (
            <ReferenceDot
              key={m.key}
              x={m.x}
              y={m.y}
              r={0}
              isFront
              ifOverflow="extendDomain"
              shape={(props) => (
                <FlowMarker
                  cx={props.cx}
                  cy={props.cy}
                  color={colors.tone[m.tone] || colors.accent}
                  glowId={ids.glow}
                  ring={colors.paper}
                  label={m.label}
                />
              )}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </>
  );
};

export default GlowArea;
